import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Wallet, Plus, Play, Download, Edit, Calendar as CalIcon, Users as UsersIcon, TrendingUp, CircleCheck, Search } from "lucide-react";
import AnimatedCounter from "@/components/app/AnimatedCounter";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CURRENCY = "₹";
const fmt = (v) => `${CURRENCY}${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Payroll() {
  const { user } = useAuth();
  const loc = useLocation();
  const isFinance = ["super_admin", "hr_admin", "finance"].includes(user?.role);
  const [summary, setSummary] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [structures, setStructures] = useState([]);
  const [slips, setSlips] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [newPeriodOpen, setNewPeriodOpen] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, pay_date: "", remarks: "" });
  const [editStruct, setEditStruct] = useState(null);
  const [structSearch, setStructSearch] = useState("");
  const [slipView, setSlipView] = useState(null);

  const load = async () => {
    try {
      if (isFinance) {
        const [sm, p, str] = await Promise.all([
          api.get("/payroll/summary"),
          api.get("/payroll/periods"),
          api.get("/payroll/structures"),
        ]);
        setSummary(sm.data); setPeriods(p.data); setStructures(str.data);
      }
      const sl = await api.get("/payroll/payslips", { params: isFinance ? {} : { scope: "mine" } });
      setSlips(sl.data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (loc.state?.openAdd && isFinance) setNewPeriodOpen(true); }, [loc.state, isFinance]);

  const createPeriod = async () => {
    try {
      await api.post("/payroll/periods", newPeriod);
      toast.success("Payroll period created");
      setNewPeriodOpen(false);
      setNewPeriod({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, pay_date: "", remarks: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runPayroll = async (pid) => {
    if (!window.confirm("Run payroll for this period? Payslips will be generated for all employees.")) return;
    try {
      const r = await api.post("/payroll/run", { period_id: pid });
      toast.success(`Generated ${r.data.generated} payslips`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveStruct = async () => {
    try {
      const clean = { ...editStruct };
      ["basic","hra","special_allowance","lta","medical","pf_employer","pf_employee","gratuity","professional_tax","income_tax","ctc"].forEach(k => {
        clean[k] = Number(clean[k] || 0);
      });
      await api.post("/payroll/structures", clean);
      toast.success("Salary structure saved");
      setEditStruct(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadSlipPDF = async (slip) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("hreye_token");
      const resp = await fetch(`${API_URL}/api/payroll/payslips/${slip.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) { toast.error("PDF generation failed"); return; }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `payslip_${slip.employee?.full_name || slip.employee_id}_${slip.year}_${MONTHS[slip.month-1]}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };

  const downloadSlip = (slip) => {
    const content = `
QrewHR · Payslip · ${MONTHS[slip.month-1]} ${slip.year}
--------------------------------------------------------
Employee: ${slip.employee?.full_name || slip.employee_id}
Designation: ${slip.employee?.designation || "—"}

EARNINGS
  Basic            ${fmt(slip.structure_snapshot?.basic)}
  HRA              ${fmt(slip.structure_snapshot?.hra)}
  Special Allow.   ${fmt(slip.structure_snapshot?.special_allowance)}
  LTA              ${fmt(slip.structure_snapshot?.lta)}
  Medical          ${fmt(slip.structure_snapshot?.medical)}
  Bonus            ${fmt(slip.bonus)}
  Reimbursement    ${fmt(slip.reimbursement)}
  ---------------------------------
  Gross            ${fmt(slip.gross + (slip.bonus||0) + (slip.reimbursement||0))}

DEDUCTIONS
  PF (Employee)    ${fmt(slip.structure_snapshot?.pf_employee)}
  Professional Tax ${fmt(slip.structure_snapshot?.professional_tax)}
  Income Tax       ${fmt(slip.structure_snapshot?.income_tax)}
  Extra deduction  ${fmt(slip.extra_deduction)}
  ---------------------------------
  Total deductions ${fmt(slip.deductions + (slip.extra_deduction||0))}

NET PAY            ${fmt(slip.net_pay)}
`;
    const blob = new Blob([content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payslip_${slip.employee?.full_name || slip.employee_id}_${slip.year}_${MONTHS[slip.month-1]}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filteredStructs = structures.filter(s => !structSearch || (s.employee?.full_name || "").toLowerCase().includes(structSearch.toLowerCase()));
  const displaySlips = selectedPeriod ? slips.filter(s => s.period_id === selectedPeriod) : slips;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="payroll-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Finance</div>
          <h1 className="text-2xl sm:text-3xl font-[Outfit] font-bold tracking-tight text-slate-900">Payroll</h1>
          <p className="text-sm text-slate-600 mt-1">{isFinance ? "Salary structures, payroll runs, and payslips" : "Your payslips and salary details"}</p>
        </div>
        {isFinance && (
          <Dialog open={newPeriodOpen} onOpenChange={setNewPeriodOpen}>
            <DialogTrigger asChild>
              <Button className="qh-grad-bg text-white font-semibold" data-testid="new-period-btn"><Plus className="w-4 h-4 mr-1"/>New payroll period</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Payroll Period</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <div><Label>Year</Label><Input type="number" value={newPeriod.year} onChange={(e) => setNewPeriod({...newPeriod, year: Number(e.target.value)})} className="mt-1"/></div>
                <div><Label>Month</Label>
                  <Select value={String(newPeriod.month)} onValueChange={(v) => setNewPeriod({...newPeriod, month: Number(v)})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Pay date</Label><Input type="date" value={newPeriod.pay_date} onChange={(e) => setNewPeriod({...newPeriod, pay_date: e.target.value})} className="mt-1"/></div>
              </div>
              <DialogFooter><Button onClick={createPeriod} className="bg-slate-900" data-testid="period-create-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary cards */}
      {isFinance && summary && (
        <div className="grid md:grid-cols-4 gap-3">
          <Card className="p-4 border-slate-200 shadow-none">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Active employees</span><UsersIcon className="w-4 h-4 text-slate-400"/></div>
            <div className="mt-2 text-3xl font-[Outfit] font-semibold text-slate-900"><AnimatedCounter value={summary.employees_total}/></div>
          </Card>
          <Card className="p-4 border-slate-200 shadow-none">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Salary mapped</span><CircleCheck className="w-4 h-4 text-emerald-500"/></div>
            <div className="mt-2 text-3xl font-[Outfit] font-semibold text-emerald-700"><AnimatedCounter value={summary.employees_with_salary}/></div>
            <div className="text-xs text-slate-500 mt-0.5">of {summary.employees_total}</div>
          </Card>
          <Card className="p-4 border-slate-200 shadow-none">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Processed periods</span><CalIcon className="w-4 h-4 text-slate-400"/></div>
            <div className="mt-2 text-3xl font-[Outfit] font-semibold text-slate-900"><AnimatedCounter value={summary.periods_processed}/></div>
            <div className="text-xs text-slate-500 mt-0.5">of {summary.periods_total}</div>
          </Card>
          <Card className="p-4 border-cyan-500/30 bg-cyan-500/5 shadow-none">
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.15em] font-semibold text-cyan-700">Latest pay-run total</span><TrendingUp className="w-4 h-4 text-cyan-600"/></div>
            <div className="mt-2 text-2xl font-[Outfit] font-semibold text-cyan-900">{fmt(summary.latest_total_paid)}</div>
            <div className="text-xs text-slate-500 mt-0.5">{summary.latest_period ? `${MONTHS[summary.latest_period.month-1]} ${summary.latest_period.year}` : "—"}</div>
          </Card>
        </div>
      )}

      <Tabs defaultValue={isFinance ? "periods" : "slips"}>
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          {isFinance && <TabsTrigger value="periods" data-testid="tab-periods">Periods</TabsTrigger>}
          {isFinance && <TabsTrigger value="structures" data-testid="tab-structures">Salary Structures</TabsTrigger>}
          <TabsTrigger value="slips" data-testid="tab-slips">{isFinance ? "Payslips" : "My Payslips"}</TabsTrigger>
        </TabsList>

        {isFinance && (
          <TabsContent value="periods" className="mt-4">
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                    <th className="px-4 py-3">Period</th><th className="px-4 py-3">Pay date</th>
                    <th className="px-4 py-3">Status</th><th className="px-4 py-3">Slips</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`period-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{MONTHS[p.month-1]} {p.year}</td>
                      <td className="px-4 py-3 text-slate-600">{p.pay_date ? new Date(p.pay_date).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3">
                        <Badge className={`border-0 capitalize ${p.status === "processed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{p.generated_count || 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {p.status !== "processed" && (
                            <Button size="sm" variant="outline" onClick={() => runPayroll(p.id)} data-testid={`run-${p.id}`}><Play className="w-3.5 h-3.5 mr-1"/>Run</Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => { setSelectedPeriod(p.id); document.querySelector('[data-testid="tab-slips"]')?.click(); }}>View slips</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {periods.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-400">No payroll periods yet.</td></tr>}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        )}

        {isFinance && (
          <TabsContent value="structures" className="mt-4">
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400"/>
                <Input placeholder="Search employee…" value={structSearch} onChange={(e) => setStructSearch(e.target.value)} className="flex-1 border-0 focus-visible:ring-0 shadow-none"/>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                      <th className="px-4 py-3">Employee</th><th className="px-4 py-3">CTC (₹/yr)</th>
                      <th className="px-4 py-3">Basic</th><th className="px-4 py-3">HRA</th>
                      <th className="px-4 py-3">Net (est.)</th><th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStructs.map((s) => {
                      const net = (s.basic||0)+(s.hra||0)+(s.special_allowance||0)+(s.lta||0)+(s.medical||0)-(s.pf_employee||0)-(s.professional_tax||200)-(s.income_tax||0);
                      return (
                        <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <Avatar className="w-7 h-7"><AvatarImage src={s.employee?.avatar_url}/><AvatarFallback className="bg-slate-900 text-white text-[10px]">{(s.employee?.full_name||"--").split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                              <div>
                                <div className="font-medium text-slate-900">{s.employee?.full_name || "—"}</div>
                                <div className="text-[11px] text-slate-500">{s.employee?.designation}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2 font-mono">{fmt(s.ctc)}</td>
                          <td className="px-4 py-2 font-mono">{fmt(s.basic)}</td>
                          <td className="px-4 py-2 font-mono">{fmt(s.hra)}</td>
                          <td className="px-4 py-2 font-mono font-semibold text-emerald-700">{fmt(net)}</td>
                          <td className="px-4 py-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => setEditStruct({ ...s, employee_id: s.employee_id })}><Edit className="w-3.5 h-3.5"/></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Edit structure dialog */}
            <Dialog open={!!editStruct} onOpenChange={(v) => !v && setEditStruct(null)}>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Salary Structure {editStruct?.employee?.full_name ? `· ${editStruct.employee.full_name}` : ""}</DialogTitle></DialogHeader>
                {editStruct && (
                  <div className="grid grid-cols-2 gap-3 py-2 max-h-[60vh] overflow-y-auto">
                    {[
                      ["ctc","Annual CTC"],["basic","Basic (monthly)"],["hra","HRA"],
                      ["special_allowance","Special Allowance"],["lta","LTA"],["medical","Medical"],
                      ["pf_employer","PF Employer"],["pf_employee","PF Employee"],
                      ["gratuity","Gratuity"],["professional_tax","Professional Tax"],["income_tax","Income Tax (TDS)"],
                    ].map(([k,l]) => (
                      <div key={k}><Label className="text-xs">{l}</Label><Input type="number" value={editStruct[k] ?? 0} onChange={(e) => setEditStruct({...editStruct, [k]: e.target.value})} className="mt-1"/></div>
                    ))}
                  </div>
                )}
                <DialogFooter><Button onClick={saveStruct} className="bg-slate-900" data-testid="struct-save">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        <TabsContent value="slips" className="mt-4 space-y-3">
          {isFinance && (
            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Filter</Label>
              <Select value={selectedPeriod || "all"} onValueChange={(v) => setSelectedPeriod(v === "all" ? "" : v)}>
                <SelectTrigger className="w-56"><SelectValue placeholder="All periods"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All periods</SelectItem>
                  {periods.map(p => <SelectItem key={p.id} value={p.id}>{MONTHS[p.month-1]} {p.year} · {p.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                    {isFinance && <th className="px-4 py-3">Employee</th>}
                    <th className="px-4 py-3">Period</th><th className="px-4 py-3">Gross</th>
                    <th className="px-4 py-3">Deductions</th><th className="px-4 py-3">Net pay</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {displaySlips.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`slip-row-${s.id}`}>
                      {isFinance && (
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-7 h-7"><AvatarImage src={s.employee?.avatar_url}/><AvatarFallback className="bg-slate-900 text-white text-[10px]">{(s.employee?.full_name||"--").split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                            <div className="font-medium text-slate-900">{s.employee?.full_name || "—"}</div>
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-slate-700">{MONTHS[s.month-1]} {s.year}</td>
                      <td className="px-4 py-3 font-mono">{fmt(s.gross)}</td>
                      <td className="px-4 py-3 font-mono text-red-600">-{fmt(s.deductions)}</td>
                      <td className="px-4 py-3 font-mono font-semibold text-emerald-700">{fmt(s.net_pay)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setSlipView(s)} data-testid={`slip-view-${s.id}`}>View</Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadSlipPDF(s)} title="Download PDF" data-testid={`slip-pdf-${s.id}`}><Download className="w-3.5 h-3.5"/></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {displaySlips.length === 0 && <tr><td colSpan={isFinance ? 6 : 5} className="p-10 text-center text-slate-400">No payslips to show.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payslip viewer */}
      <Dialog open={!!slipView} onOpenChange={(v) => !v && setSlipView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Payslip · {slipView && `${MONTHS[slipView.month-1]} ${slipView.year}`}</DialogTitle></DialogHeader>
          {slipView && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-md">
                <Avatar className="w-10 h-10"><AvatarImage src={slipView.employee?.avatar_url}/><AvatarFallback className="bg-slate-900 text-white text-xs">{(slipView.employee?.full_name||"--").split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                <div>
                  <div className="font-medium text-slate-900">{slipView.employee?.full_name}</div>
                  <div className="text-xs text-slate-500">{slipView.employee?.designation}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Earnings</div>
                  <div className="space-y-1 text-sm">
                    {[["Basic","basic"],["HRA","hra"],["Special","special_allowance"],["LTA","lta"],["Medical","medical"]].map(([l,k]) => (
                      <div key={k} className="flex justify-between"><span className="text-slate-600">{l}</span><span className="font-mono">{fmt(slipView.structure_snapshot?.[k])}</span></div>
                    ))}
                    {slipView.bonus > 0 && <div className="flex justify-between text-emerald-700"><span>Bonus</span><span className="font-mono">+{fmt(slipView.bonus)}</span></div>}
                    {slipView.reimbursement > 0 && <div className="flex justify-between text-emerald-700"><span>Reimbursement</span><span className="font-mono">+{fmt(slipView.reimbursement)}</span></div>}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Deductions</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">PF</span><span className="font-mono">{fmt(slipView.structure_snapshot?.pf_employee)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Professional Tax</span><span className="font-mono">{fmt(slipView.structure_snapshot?.professional_tax)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Income Tax</span><span className="font-mono">{fmt(slipView.structure_snapshot?.income_tax)}</span></div>
                    {slipView.extra_deduction > 0 && <div className="flex justify-between text-red-700"><span>Extra</span><span className="font-mono">-{fmt(slipView.extra_deduction)}</span></div>}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-md">
                <span className="text-sm font-semibold text-emerald-800">Net pay</span>
                <span className="text-2xl font-[Outfit] font-semibold text-emerald-900">{fmt(slipView.net_pay)}</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => downloadSlip(slipView)} variant="outline"><Download className="w-4 h-4 mr-1"/>.txt</Button>
                <Button onClick={() => downloadSlipPDF(slipView)} className="bg-slate-900 hover:bg-slate-800"><Download className="w-4 h-4 mr-1"/>Download PDF</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
